"use client";

import { type ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { isNative } from '@/lib/native';
import './session-analytics.css';

type Clarity = ((...args: unknown[]) => void) & { q?: unknown[][] };
declare global { interface Window { clarity?: Clarity; globalPrivacyControl?: boolean; } }
const key = 'mik-session-analytics-v1';
const maxAge = 180 * 86400000;
import { hosts, paths, project } from './session-analytics-config';
// Retain this latch after stop: an async SDK request cannot be reliably cancelled.
const recorder: { path?: string; active: boolean; navigating: boolean } = { active: false, navigating: false };
const optedOut = () => (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true || navigator.doNotTrack === '1' || (window as Window & { doNotTrack?: string }).doNotTrack === '1';
function choice() {
  try { const v = JSON.parse(localStorage.getItem(key) || 'null'); return v && ['granted', 'denied'].includes(v.choice) && Number.isFinite(v.at) && Date.now() >= v.at && Date.now() - v.at < maxAge ? v.choice as string : null; } catch { return null; }
}
function save(value: string) { try { localStorage.setItem(key, JSON.stringify({ choice: value, at: Date.now() })); } catch { /* This document only. */ } }
function safeUrl(value: string) {
  try { const u = new URL(value, location.href); return !u.search && (!u.hash || (u.origin === location.origin && /^[a-z][a-z0-9_-]{0,60}$/i.test(u.hash.slice(1)) && !!document.getElementById(u.hash.slice(1)))); } catch { return false; }
}
function safeReferrer() {
  if (!document.referrer) return true;
  try { const u = new URL(document.referrer); return safeUrl(u.href) && (u.origin === location.origin ? paths.includes(u.pathname) : u.pathname === '/'); } catch { return false; }
}
function hasPrivateSession() {
  try { return /(?:^|;\s*)sb-[^=;]+-auth-token(?:\.\d+)?=/.test(document.cookie) || Object.keys(localStorage).some(k => /auth-token|impersonation/i.test(k)); } catch { return true; }
}
function clearCookies() {
  for (const name of ['_clck', '_clsk']) for (const domain of ['', ...hosts.map(h => `; Domain=${h}`), ...hosts.map(h => `; Domain=.${h}`)]) document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax; Secure${domain}`;
}
function halt() { if (recorder.active) window.clarity?.('stop'); recorder.active = false; }
function revoke() {
  if (recorder.path) window.clarity?.('consentv2', { analytics_Storage: 'denied', ad_Storage: 'denied' });
  halt(); clearCookies();
}
function navigate(url: string, replace = false) { recorder.navigating = true; halt(); if (replace) location.replace(url); else location.assign(url); }
export function eligible() {
  return location.protocol === 'https:' && hosts.includes(location.hostname) && paths.includes(location.pathname)
    && safeUrl(location.href) && safeReferrer() && !optedOut() && !isNative()
    && !hasPrivateSession() && !navigator.webdriver && !/bot|crawler|spider|headless/i.test(navigator.userAgent)
    && !/(?:^|;\s*)mik_ab_qa=/.test(document.cookie);
}
function start(path: string) {
  if (recorder.path || !eligible() || !/^[a-z0-9]+$/.test(project)) return;
  recorder.path = path;
  recorder.active = true;
  document.body.setAttribute('data-clarity-mask', 'true');
  window.clarity ||= Object.assign((...args: unknown[]) => { window.clarity!.q ||= []; window.clarity!.q.push(args); }, { q: [] });
  window.clarity('consentv2', { analytics_Storage: 'granted', ad_Storage: 'denied' });
  // Every navigation unloads the SDK. Next's root barrier below also prevents
  // a programmatic transition from mounting private children before history updates.
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = history[method].bind(history);
    history[method] = (data: unknown, unused: string, url?: string | URL | null) => {
      if (url != null && new URL(url, location.href).href !== location.href) { navigate(new URL(url, location.href).href, method === 'replaceState'); return; }
      original(data, unused, url);
    };
  }
  document.addEventListener('click', event => {
    const a = (event.target as Element).closest?.('a[href]') as HTMLAnchorElement | null;
    if (!a || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || a.target === '_blank' || a.hasAttribute('download')) return;
    const url = new URL(a.href, location.href);
    if (url.origin === location.origin && url.href !== location.href) { event.preventDefault(); event.stopImmediatePropagation(); navigate(url.href); }
  }, true);
  window.addEventListener('popstate', () => { recorder.navigating = true; halt(); location.reload(); }, true);
  window.addEventListener('hashchange', () => { if (!safeUrl(location.href)) { recorder.navigating = true; halt(); location.reload(); } });
  const tag = document.createElement('script');
  tag.async = true; tag.src = 'https://www.clarity.ms/tag/' + project; tag.referrerPolicy = 'no-referrer';
  document.head.appendChild(tag);
}

/** New route content stays unmounted until a fresh document without the SDK. */
export function SessionAnalyticsBoundary({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const reload = Boolean(recorder.path && recorder.path !== pathname);
  useLayoutEffect(() => { if (reload && !recorder.navigating) navigate(pathname); }, [reload, pathname]);
  return reload ? null : <>{children}<SessionAnalytics pathname={pathname} /></>;
}
function SessionAnalytics({ pathname }: { pathname: string }) {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  useEffect(() => {
    // Public information pages only. The marketplace, store/cart, auth, orders,
    // reservations, account and admin routes never load the recorder.
    const enabled = hosts.includes(location.hostname) && paths.includes(location.pathname) && !isNative();
    setVisible(enabled && !hasPrivateSession());
    if (!enabled) return;
    const current = choice();
    const granted = current === 'granted' && !optedOut();
    setAllowed(granted); setOpen(current === null && !optedOut());
    if (granted) start(pathname);
    if (optedOut()) clearCookies();
    const storage = (e: StorageEvent) => { if (hasPrivateSession()) { setVisible(false); if (recorder.path) { e.stopImmediatePropagation(); revoke(); location.reload(); } } else if ((e.key === key || e.key === null) && choice() !== 'granted') { revoke(); setAllowed(false); if (recorder.path) location.reload(); } };
    const show = () => { if (optedOut() || (recorder.path && choice() !== 'granted')) { revoke(); setAllowed(false); if (recorder.path) location.reload(); } };
    window.addEventListener('storage', storage, true); window.addEventListener('pageshow', show);
    return () => { window.removeEventListener('storage', storage, true); window.removeEventListener('pageshow', show); };
  }, [pathname]);
  if (!visible) return null;
  const decline = () => { save('denied'); setAllowed(false); setOpen(false); revoke(); if (recorder.path) location.reload(); };
  const allow = () => { if (!optedOut()) { save('granted'); setAllowed(true); setOpen(false); start(pathname); } };
  return <section className="session-privacy" aria-label="Website privacy">
    <details open={privacy} onToggle={e => setPrivacy(e.currentTarget.open)}>
      <summary>Privacy &amp; session analytics</summary>
      <p>We use Google Analytics to measure website visits and interactions. With your permission, Microsoft Clarity collects clicks, scrolling and masked session replays on public information pages to help us improve this website. Clarity sends this usage data to Microsoft and uses analytics cookies after you allow it. Microsoft may also use the collected data for product improvement and Microsoft Advertising, as described in its Privacy Statement. Our integration denies advertising storage and does not send custom visitor identifiers.</p>
      <p>You can decline or turn off Clarity at any time. Browser Global Privacy Control and Do Not Track preferences are respected. Account, shopping, payment, order and private pages are excluded. Visits with query strings are also excluded. This choice does not change the existing Google Analytics setup.</p>
      <p><a href="https://privacy.microsoft.com/en-us/privacystatement" target="_blank" rel="noopener noreferrer">Microsoft Privacy Statement</a> · <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></p>
    </details>
    <button type="button" onClick={() => setOpen(true)}>Session analytics settings</button>
    {open && <aside className="session-choice" aria-label="Optional session analytics">
      <p><strong>Help us improve this website</strong>Allow Microsoft Clarity to collect clicks, scrolling and masked session replays? <button type="button" onClick={() => { setPrivacy(true); setOpen(false); }}>Privacy details</button></p>
      <div className="session-choice-actions"><button type="button" onClick={decline}>{allowed ? 'Turn off' : 'No thanks'}</button><button type="button" onClick={allow} disabled={optedOut()}>Allow</button></div>
      <p id="session-analytics-status" role="status">{optedOut() ? 'Your browser privacy preference is respected. Session analytics is off.' : allowed ? 'Session analytics is allowed. You can turn it off here at any time.' : 'Session analytics is off.'}</p>
    </aside>}
  </section>;
}
